import { motion, useTransform, cubicBezier, type MotionValue } from 'motion/react'
import type { DerivedTask } from './fixtures'
import { cardRange } from './beats'
import { ease } from '../../../lib/motion'

const PRIORITY_STYLES: Record<DerivedTask['priority'], string> = {
  high: 'bg-red-500/10 text-red-300 ring-red-400/25',
  medium: 'bg-amber-500/10 text-amber-300 ring-amber-400/25',
  low: 'bg-emerald-500/10 text-emerald-300 ring-emerald-400/25',
}

const PRIORITY_LABEL: Record<DerivedTask['priority'], string> = {
  high: 'High',
  medium: 'Medium',
  low: 'Low',
}

// One easing curve for every scrubbed transform in this file, matching the
// project's `ease.standard` token so the card's motion reads as part of the
// same system as the rest of the page rather than inventing a bespoke feel.
const easeStandard = cubicBezier(...ease.standard)

type Props = {
  task: DerivedTask
  index: number
  /** Total number of cards. Required so every card gets an equal window. */
  count: number
  progress: MotionValue<number>
}

export default function TaskCard({ task, index, count, progress }: Props) {
  const [liftStart, liftEnd] = cardRange('lift', index, count)
  const [, landEnd] = cardRange('land', index, count)

  // Lift out of the transcript, then settle into the board column.
  const opacity = useTransform(progress, [liftStart, liftEnd], [0, 1], {
    ease: easeStandard,
  })
  // Settles to 0, not to a per-index offset. An `index * 4` landing offset
  // pushed the last card 12px below its laid-out position, which is exactly
  // the board panel's `-inset-3` padding — so at the end of the scroll the
  // bottom card's border collided with the panel's rounded edge.
  const y = useTransform(progress, [liftStart, liftEnd], [24, 0], {
    ease: easeStandard,
  })
  const x = useTransform(progress, [liftStart, landEnd], [-32, 0], {
    ease: easeStandard,
  })
  const scale = useTransform(progress, [liftStart, liftEnd], [0.96, 1], {
    ease: easeStandard,
  })

  // Metadata arrives after the card body, so the card reads as "filling in".
  const metaOpacity = useTransform(
    progress,
    [liftStart + (liftEnd - liftStart) * 0.5, liftEnd],
    [0, 1],
    { ease: easeStandard }
  )

  return (
    <motion.div
      style={{ opacity, y, x, scale }}
      className="rounded-xl border border-white/[0.08] bg-surface-overlay p-3.5 shadow-glow-accent"
    >
      <p className="text-body font-medium text-white">{task.title}</p>

      <motion.div
        style={{ opacity: metaOpacity }}
        className="mt-3 flex flex-wrap items-center gap-2"
      >
        <span
          className={`rounded px-1.5 py-0.5 text-caption font-medium ring-1 ring-inset ${
            PRIORITY_STYLES[task.priority]
          }`}
        >
          {PRIORITY_LABEL[task.priority]}
        </span>
        <span className="text-caption text-white/55">{task.assignee}</span>
        <span className="text-caption text-white/25">·</span>
        <span className="text-caption text-white/55">{task.due}</span>
      </motion.div>
    </motion.div>
  )
}
